import { useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchOfferTags } from 'actions/OfferActions'
import useQueryParams from 'hooks/useQueryParams'
import moment from 'moment'

interface UseOfferTagsResult {
  tags: Array<{ id: string; name: string }>
  fetching: boolean
}

/**
 * Hook to fetch and access offer tags from Redux store
 * @param offerId - The offer ID to fetch tags for
 * @returns Object containing tags array and fetching status
 */
function useOfferTags(offerId: string | undefined): UseOfferTagsResult {
  const dispatch = useAppDispatch()
  const queryParams = useQueryParams()

  const travelStartDate = useMemo(() => {
    const checkInDate = queryParams.get('checkIn')
    return checkInDate ? moment(checkInDate).utc().startOf('day').toISOString() : undefined
  }, [queryParams])

  const travelEndDate = useMemo(() => {
    const checkOutDate = queryParams.get('checkOut')
    return checkOutDate ? moment(checkOutDate).utc().startOf('day').toISOString() : undefined
  }, [queryParams])

  const cacheKey = useMemo(() => {
    if (!offerId) return undefined
    const datePart = [travelStartDate, travelEndDate].filter(Boolean).join(':')
    return datePart ? `${offerId}:${datePart}` : offerId
  }, [offerId, travelStartDate, travelEndDate])

  const tagData = useAppSelector(state => {
    if (!cacheKey) {
      return undefined
    }
    return state.offer.offerTags?.[cacheKey]
  })

  useEffect(() => {
    if (!offerId) {
      return
    }

    dispatch(fetchOfferTags(offerId, { travelStartDate, travelEndDate, travelDateOverlap: true }))
  }, [dispatch, offerId, travelStartDate, travelEndDate])

  return {
    tags: tagData?.tags ?? [],
    fetching: tagData?.fetching ?? false,
  }
}

export default useOfferTags
